import React, { useEffect, useState } from 'react';
import './QuotesNotifications.css';
import { can } from '../../utils/permissions';
import { fetchAPI, postAPI, deleteAPI } from '../../utils/utils';
import { getCastellerDisplayName } from '../../utils/castellerName';

function QuotesNotifications({ userInfo }) {
    const [recipients, setRecipients] = useState([]);
    const [potentialRecipients, setPotentialRecipients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saveStatus, setSaveStatus] = useState({ message: '', type: '' });

    const isAdmin = can(userInfo, 'MANAGE_QUOTES');

    const loadRecipients = () => {
        fetchAPI('/quotes/notification-recipients', (data) => {
            setRecipients(data.recipients || []);
            setPotentialRecipients(data.potentialRecipients || []);
            setLoading(false);
        }, () => {
            setLoading(false);
        });
    };

    useEffect(() => {
        if (!isAdmin) return;
        loadRecipients();
    }, [isAdmin]);

    const toggleRecipient = (user, isCurrentRecipient) => {
        if (loading) return;
        setLoading(true);

        const userId = user.id || user.user_id;

        if (isCurrentRecipient) {
            deleteAPI(`/quotes/notification-recipients/${userId}`, (response) => {
                if (response.success) {
                    setSaveStatus({
                        message: 'Destinatari eliminat correctament',
                        type: 'success'
                    });
                    loadRecipients();
                } else {
                    setLoading(false);
                    setSaveStatus({
                        message: response.message || 'Error eliminant destinatari',
                        type: 'error'
                    });
                }
                setTimeout(() => setSaveStatus({ message: '', type: '' }), 3000);
            }, () => {
                setLoading(false);
                setSaveStatus({
                    message: 'Error eliminant destinatari',
                    type: 'error'
                });
                setTimeout(() => setSaveStatus({ message: '', type: '' }), 3000);
            });
        } else {
            postAPI('/quotes/notification-recipients', { userId }, (response) => {
                if (response.success) {
                    setSaveStatus({
                        message: 'Destinatari afegit correctament',
                        type: 'success'
                    });
                    loadRecipients();
                } else {
                    setLoading(false);
                    setSaveStatus({
                        message: response.message || 'Error afegint destinatari',
                        type: 'error'
                    });
                }
                setTimeout(() => setSaveStatus({ message: '', type: '' }), 3000);
            }, () => {
                setLoading(false);
                setSaveStatus({
                    message: 'Error afegint destinatari',
                    type: 'error'
                });
                setTimeout(() => setSaveStatus({ message: '', type: '' }), 3000);
            });
        }
    };

    const getDisplayName = (user) => {
        if (!user) return '';
        return getCastellerDisplayName(user, `Usuari ${user.id}`);
    };

    const getAllAdmins = () => {
        const recipientIds = new Set(recipients.map(r => r.user_id));

        const allAdmins = [...recipients.map(r => ({
            ...r,
            id: r.user_id,
            isRecipient: true
        }))];

        potentialRecipients.forEach(admin => {
            if (!recipientIds.has(admin.id)) {
                allAdmins.push({
                    ...admin,
                    isRecipient: admin.is_recipient === 1
                });
            }
        });

        return allAdmins.sort((a, b) => {
            if (a.isRecipient !== b.isRecipient) {
                return a.isRecipient ? -1 : 1;
            }
            return getDisplayName(a).localeCompare(getDisplayName(b));
        });
    };

    if (!isAdmin) {
        return (
            <div className="notifications-container">
                <div className="unauthorized-message">
                    <p>No tens permís per gestionar les notificacions.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="notifications-container">
            <h2>Configuració de notificacions de quotes</h2>
            <p className="notifications-description">
                Gestiona quins responsables rebran notificacions quan es pagui una quota o una subscripció.
            </p>

            {saveStatus.message && (
                <div className={`status-message ${saveStatus.type}`}>
                    {saveStatus.message}
                </div>
            )}

            {loading ? (
                <div className="loading-container">
                    <div className="spinner"></div>
                    <p>Carregant...</p>
                </div>
            ) : (
                <div className="notification-settings">
                    <div className="unified-admin-list">
                        <h3>Responsables de quotes</h3>
                        <p className="subtitle">Selecciona els responsables que rebran notificacions.</p>

                        {getAllAdmins().length === 0 ? (
                            <p className="empty-list-message">
                                No s'han trobat responsables.
                            </p>
                        ) : (
                            <ul className="admin-list">
                                {getAllAdmins().map(admin => {
                                    const isRecipient = admin.isRecipient;

                                    return (
                                        <li key={admin.id || admin.user_id} className={`admin-item ${isRecipient ? 'is-recipient' : ''}`}>
                                            <label className="admin-toggle">
                                                <input
                                                    type="checkbox"
                                                    checked={isRecipient}
                                                    onChange={() => toggleRecipient(admin, isRecipient)}
                                                    disabled={loading}
                                                />
                                                <span className="admin-name">{getDisplayName(admin)}</span>
                                            </label>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}

                        {recipients.length === 0 && (
                            <p className="info-message">
                                Si no selecciones cap destinatari, s'enviaran notificacions a tots els responsables.
                            </p>
                        )}
                    </div>
                </div>
            )}

            <div className="notification-info">
                <h3>Com funcionen les notificacions?</h3>
                <ul>
                    <li>Quan algú paga una quota, els destinataris configurats rebran una notificació.</li>
                    <li>Quan algú pausa una subscripció, també es notificarà als responsables.</li>
                    <li>Quan es cobra automàticament una subscripció, s'enviarà una notificació.</li>
                    <li>Si no hi ha destinataris configurats, les notificacions s'enviaran a tots els responsables.</li>
                    <li>Per rebre notificacions, l'usuari ha de tenir l'aplicació mòbil instal·lada i haver acceptat les notificacions.</li>
                </ul>
            </div>
        </div>
    );
}

export default QuotesNotifications;
